import { useState } from "react";
import { useParams } from "react-router-dom";
import { useEvidenceAsk, type EvidenceHit } from "../../hooks/useEvidenceAsk";
import { useChunkContext } from "../../hooks/useChunkContext";

/**
 * Reusable "search the corpus for this" control. Seeds the live retriever
 * (retriever-query, mode 'find' = semantic + full-text, no LLM answer) with
 * the given text and shows the ranked evidence hits inline, each openable to
 * its source. Drop it on any card — an Oathbreaker allegation, an Archers
 * claim element, etc. — passing the relevant text as `seed`.
 */
export default function CorpusSearch({ seed, label = "⌕ search the corpus for this" }: { seed: string; label?: string }) {
  const { caseId } = useParams<{ caseId: string }>();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(seed);
  const { ask, result, asking, error } = useEvidenceAsk(caseId);

  const run = (q: string) => { const t = q.trim(); if (t.length < 3) return; void ask(t.slice(0, 2000), "find"); };

  if (!open) {
    return (
      <button type="button" onClick={() => { setOpen(true); run(seed); }}
        className="mt-3 flex items-center gap-1.5 rounded border border-blue-bright/40 bg-blue-bright/5 px-2.5 py-1 font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/90 hover:bg-blue-bright/15">
        {label}
      </button>
    );
  }
  return (
    <div className="mt-3 rounded border border-blue-bright/30 bg-black/20 px-3 py-2.5">
      <form onSubmit={(e) => { e.preventDefault(); run(query); }} className="flex items-center gap-2">
        <span className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/70">// ask the corpus:</span>
        <input value={query} onChange={(e) => setQuery(e.target.value)}
          className="flex-1 rounded border border-steel-dark/60 bg-charcoal/70 px-2 py-1 font-display text-[12px] text-bone placeholder:text-bone-mute/50 focus:border-blue-bright/50 focus:outline-none" />
        <button type="submit" disabled={asking || query.trim().length < 3}
          className="rounded border border-blue-bright/40 bg-blue-bright/10 px-2.5 py-1 font-display text-sm font-light uppercase tracking-[0.1em] text-bone hover:bg-blue-bright/25 disabled:opacity-50">
          {asking ? "…" : "search"}
        </button>
        <button type="button" onClick={() => setOpen(false)}
          className="rounded border border-steel-dark/60 px-2 py-1 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute hover:text-bone">
          close
        </button>
      </form>
      <div className="mt-2 space-y-2">
        {asking && <p className="font-display text-sm text-bone-mute">// raking the corpus — semantic + exact…</p>}
        {error && <p className="font-display text-sm text-ember-bright">// {error}</p>}
        {result && !asking && result.hits.length === 0 && (
          <p className="font-display text-sm text-bone-dim">// no scents in the corpus for that.</p>
        )}
        {result && result.hits.map((h) => <CorpusHit key={h.chunk_id} h={h} />)}
      </div>
    </div>
  );
}

function CorpusHit({ h }: { h: EvidenceHit }) {
  const [open, setOpen] = useState(false);
  const ctx = useChunkContext(h.chunk_id, open);
  return (
    <div className="rounded border border-steel-dark/70 bg-iron-2/40 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-bright" />
        <span>{h.source_label}</span>
        {h.occurred_on && <span>· {h.occurred_on}</span>}
        <span className="ml-auto text-bone-mute/60">scent {Math.round(h.score * 100)}</span>
      </div>
      <p className="mt-1 border-l-2 border-steel-dark pl-2 font-display text-[12px] leading-snug text-bone-dim">{h.snippet}</p>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="mt-1.5 font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/80 hover:text-blue-bright">
        {open ? "▾ hide source" : "▸ view in source"}
      </button>
      {open && (
        <div className="mt-2 rounded border border-steel-dark/50 bg-charcoal/60 p-2">
          {ctx.isLoading && <p className="font-display text-sm text-bone-mute">// opening the source…</p>}
          {ctx.error && <p className="font-display text-sm text-ember-bright">// {ctx.error.message}</p>}
          {ctx.data && (
            <div className="space-y-1">
              {ctx.data.chunks.map((line) => (
                <pre key={line.chunk_index}
                     className={`overflow-x-auto whitespace-pre-wrap rounded px-2 py-1 font-display text-base leading-snug ${
                       line.is_center ? "bg-ember/10 text-bone shadow-ember-soft" : "text-bone-mute/70"
                     }`}>
                  {line.body}
                </pre>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
